/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { RelationType } from '../../@types/event';
import type { XHubChatEvent } from '../event/event';

/**
 * Checks if a value matches a given field value, which may be a * terminated
 * wildcard pattern.
 * @param actualValue -  The value to be compared
 * @param filterValue -  The filter pattern to be compared
 * @returns true if the actualValue matches the filterValue
 */
function matchesWildcard(actualValue: string, filterValue: string): boolean {
  if (filterValue.endsWith('*')) {
    const typePrefix = filterValue.slice(0, -1);
    return actualValue.slice(0, typePrefix.length) === typePrefix;
  } else {
    return actualValue === filterValue;
  }
}

export type IFilterComponent = {
  'types'?: string[];
  'not_types'?: string[];
  'rooms'?: string[];
  'not_rooms'?: string[];
  'senders'?: string[];
  'not_senders'?: string[];
  'contains_url'?: boolean;
  'limit'?: number;
  'related_by_senders'?: Array<RelationType | string>;
  'related_by_rel_types'?: string[];

  // Unstable values
  'io.element.relation_senders'?: Array<RelationType | string>;
  'io.element.relation_types'?: string[];
};

/**
 * FilterComponent is a section of a Filter definition which defines the
 * types, rooms, senders filters etc to be applied to a particular type of resource.
 * This is all ported over from synapse's Filter object.
 *
 * N.B. that synapse refers to these as 'Filters', and what js-sdk refers to as
 * 'Filters' are referred to as 'FilterCollections'.
 */
export class FilterComponent {
  public constructor(
    private filterJson: IFilterComponent,
    public readonly userId?: string | undefined | null,
  ) {}

  /**
   * Checks with the filter component matches the given event
   * @param event - event to be checked against the filter
   * @returns true if the event matches the filter
   */
  public check(event: XHubChatEvent): boolean {
    const bundledRelationships = event.getUnsigned()?.['m.relations'] || {};
    const relations: Array<string | RelationType> = Object.keys(bundledRelationships);
    // Relation senders allows in theory a look-up of any senders
    // however clients can only know about the current user participation status
    // as sending a whole list of participants could be proven problematic in terms
    // of performance
    // This should be improved when bundled relationships solve that problem
    const relationSenders: string[] = [];
    if (this.userId) {
      relationSenders.push(this.userId);
    }

    return this.checkFields(
      event.getRoomId(),
      event.getSender(),
      event.getType(),
      event.getContent() ? event.getContent().url !== undefined : false,
      relations,
      relationSenders,
    );
  }

  /**
   * Converts the filter component into the form expected over the wire
   */
  public toJSON(): object {
    return Object.fromEntries(
      Object.entries({
        types: this.filterJson.types,
        not_types: this.filterJson.not_types,
        rooms: this.filterJson.rooms,
        not_rooms: this.filterJson.not_rooms,
        senders: this.filterJson.senders,
        not_senders: this.filterJson.not_senders,
        contains_url: this.filterJson.contains_url,
      }).filter(([_key, value]) => value),
    );
  }

  /**
   * Checks whether the filter component matches the given event fields.
   * @param roomId -        the roomId for the event being checked
   * @param sender -        the sender of the event being checked
   * @param eventType -     the type of the event being checked
   * @param containsUrl -  whether the event contains a content.url field
   * @param relationTypes -  whether has aggregated relation of the given type
   * @param relationSenders - whether one of the relation is sent by the user listed
   * @returns true if the event fields match the filter
   */
  private checkFields(
    roomId: string | undefined,
    sender: string | undefined,
    eventType: string,
    containsUrl: boolean,
    relationTypes: Array<RelationType | string>,
    relationSenders: string[],
  ): boolean {
    const literalKeys = {
      rooms(v: string): boolean {
        return roomId === v;
      },
      senders(v: string): boolean {
        return sender === v;
      },
      types(v: string): boolean {
        return matchesWildcard(eventType, v);
      },
    } as const;

    for (const name in literalKeys) {
      const matchFunc = literalKeys[<keyof typeof literalKeys>name];
      const notName = `not_${name}`;
      const disallowedValues = this.filterJson[<`not_${keyof typeof literalKeys}`>notName];
      if (disallowedValues?.some(matchFunc)) {
        return false;
      }

      const allowedValues = this.filterJson[name as keyof typeof literalKeys];
      if (allowedValues && !allowedValues.some(matchFunc)) {
        return false;
      }
    }

    const containsUrlFilter = this.filterJson.contains_url;
    if (containsUrlFilter !== undefined && containsUrlFilter !== containsUrl) {
      return false;
    }

    return true;
  }

  private arrayMatchesFilter(filter: any[], values: any[]): boolean {
    return (
      values.length > 0
      && filter.every((value) => {
        return values.includes(value);
      })
    );
  }

  /**
   * Filters a list of events down to those which match this filter component
   * @param events -  Events to be checked against the filter component
   * @returns events which matched the filter component
   */
  public filter(events: XHubChatEvent[]): XHubChatEvent[] {
    return events.filter(this.check, this);
  }

  /**
   * Returns the limit field for a given filter component, providing a default of
   * 10 if none is otherwise specified. Cargo-culted from Synapse.
   * @returns the limit for this filter component.
   */
  public limit(): number {
    return this.filterJson.limit !== undefined ? this.filterJson.limit : 10;
  }
}
